<template>
  <div class="wf-form-designer">
    <!-- 顶部工具栏 -->
    <div class="wf-toolbar">
      <el-input v-model="formName" size="small" placeholder="表单名称" style="width: 220px" maxlength="50" />
      <el-input v-model="remark" size="small" placeholder="备注(可选)" style="width: 220px; margin-left: 8px" maxlength="100" />
      <div class="wf-toolbar-right">
        <el-button size="small" @click="$emit('cancel')">取消</el-button>
        <el-button size="small" type="primary" icon="el-icon-check" :loading="saving" @click="save">保存</el-button>
      </div>
    </div>

    <div class="wf-body">
      <!-- 左：字段库 -->
      <div class="wf-palette">
        <div v-for="g in groups" :key="g.title" class="wf-palette-group">
          <div class="wf-palette-title">{{ g.title }}</div>
          <draggable
            :list="g.items"
            :group="{ name: 'wf-fields', pull: 'clone', put: false }"
            :clone="cloneField"
            :sort="false"
            class="wf-palette-items"
          >
            <div
              v-for="it in g.items"
              :key="it.type"
              class="wf-palette-item"
              @click="addField(it.type)"
            >
              <i :class="it.icon"></i> {{ it.label }}
            </div>
          </draggable>
        </div>
      </div>

      <!-- 中：画布 -->
      <div class="wf-canvas-wrap">
        <div class="wf-canvas-title">{{ formName || '未命名表单' }}</div>
        <draggable v-model="fields" group="wf-fields" class="wf-canvas" handle=".wf-drag-handle">
          <div
            v-for="(f, idx) in fields"
            :key="f.key"
            class="wf-canvas-item"
            :class="{ active: selectedKey === f.key }"
            @click="selectField(f)"
          >
            <i class="el-icon-rank wf-drag-handle" @click.stop></i>
            <div class="wf-canvas-main">
              <div class="wf-canvas-label">
                <span v-if="f.required && f.type !== 'tips'" class="wf-req">*</span>{{ f.label }}
                <em v-if="f.requiredWhen && f.requiredWhen.key" class="wf-cond-mark">条件必填</em>
                <em v-if="f.visible === false" class="wf-cond-mark">已隐藏</em>
                <em v-if="f.type === 'readonlyText'" class="wf-cond-mark">只读</em>
                <em v-if="f.type === 'user' && f.deptFilterKey" class="wf-cond-mark">{{ deptFilterLabel(f) }}</em>
                <em v-if="f.type === 'dept' && hasCenterScope(f)" class="wf-cond-mark">{{ deptCenterLabel(f) }}</em>
              </div>
              <div class="wf-canvas-preview">
                <span v-if="f.type === 'tips'" class="wf-tips-preview"><i class="el-icon-info"></i> {{ f.content }}</span>
                <div v-else-if="f.type === 'detail'" class="wf-detail-preview">
                  <div class="wf-detail-head">
                    <span
                      v-for="(c, ci) in (f.columns || [])"
                      :key="c.key"
                      class="wf-detail-th"
                      :style="c.width ? { flex: '0 0 ' + c.width + 'px', width: c.width + 'px' } : {}"
                    >
                      <em v-if="c.required" class="wf-req">*</em>{{ c.label }}
                      <i
                        class="wf-col-resizer"
                        :class="{ active: resizingColKey === f.key + '_' + ci }"
                        title="拖动调整列宽"
                        @mousedown.stop.prevent="startColResize(f, ci, $event)"
                        @click.stop
                      ></i>
                    </span>
                  </div>
                  <div class="wf-detail-add">+ 增加明细</div>
                </div>
                <el-cascader
                  v-else-if="f.type === 'cascade'"
                  disabled
                  :props="{ expandTrigger: 'hover' }"
                  :placeholder="'级联选择(' + ((f.levels || []).length || 0) + '级)'"
                  style="width: 100%"
                />
                <el-input
                  v-else-if="f.type === 'curUser'"
                  size="mini"
                  placeholder="自动获取当前登录用户"
                  disabled
                />
                <el-input
                  v-else-if="f.type === 'userRole'"
                  size="mini"
                  placeholder="自动获取当前登录用户角色"
                  disabled
                />
                <el-input
                  v-else-if="f.type === 'total'"
                  size="mini"
                  placeholder="自动计算 Σ(数量×单价)"
                  disabled
                />
                <el-input
                  v-else-if="f.type === 'sqlValue'"
                  size="mini"
                  placeholder="自动取值(自定义SQL)"
                  disabled
                />
                <el-input-number
                  v-else-if="f.type === 'ctrlNumber'"
                  size="mini"
                  disabled
                  controls-position="right"
                  placeholder="受控数字"
                  style="width: 100%"
                />
                <div v-else-if="f.type === 'money'" class="wf-canvas-money">
                  <el-input size="mini" value="1,234.56" disabled style="width: 220px" />
                  <em v-if="f.unit" class="wf-unit">{{ f.unit }}</em>
                  <div v-if="f.showUpper !== false" class="wf-money-upper">大写：壹仟贰佰叁拾肆元伍角陆分</div>
                </div>
                <div v-else-if="f.type === 'upload'" class="wf-canvas-upload">
                  <el-button size="mini" icon="el-icon-upload2" disabled>点击上传</el-button>
                  <div v-if="uploadTip(f)" class="wf-upload-tip">支持格式：{{ uploadTip(f) }}</div>
                </div>
                <span v-else-if="f.type === 'readonlyText'" class="wf-tips-preview">{{ f.content || '（未填写显示内容）' }}</span>
                <el-input v-else size="mini" :placeholder="f.placeholder || typeLabel(f.type)" disabled />
              </div>
            </div>
            <div class="wf-canvas-ops" @click.stop>
              <i class="el-icon-document-copy" title="复制" @click="copyField(idx)"></i>
              <i class="el-icon-delete" title="删除" @click="removeField(idx)"></i>
            </div>
          </div>
        </draggable>
        <div v-if="!fields.length" class="wf-canvas-empty">从左侧点击或拖拽字段到此处</div>
      </div>

      <!-- 右：属性 -->
      <div class="wf-config">
        <FieldConfig
          :field="selectedField"
          :fields="fields"
          :role-options="roleOptions"
          :center-options="centerOptions"
          @remove="removeSelected"
        />
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import FieldConfig from './components/designer/form/FieldConfig.vue'
import { FIELD_GROUPS, createField, typeLabel, attachTipText, readonlyTextPlaceholders } from './components/designer/form/fieldTypes'
import { getForm, saveForm } from '@/api/wflow/form'
import { getTRoleSelectByPage } from '@/api/plat/PosMana'
import { getDepartmentList } from '@/api/plat/DepMana'

export default {
  name: 'WflowFormDesigner',
  components: { draggable, FieldConfig },
  props: {
    formId: { type: [Number, String], default: null }
  },
  data() {
    return {
      groups: FIELD_GROUPS,
      fields: [],
      formName: '',
      remark: '',
      id: null,
      status: 0,
      version: 0,
      selectedKey: '',
      saving: false,
      roleOptions: [],
      // 一级组织（中心）选项，供「部门」字段配置中心范围
      centerOptions: [],
      // 正在拖动列宽的列标识 fieldKey_colIndex
      resizingColKey: ''
    }
  },
  computed: {
    selectedField() {
      return this.fields.find(f => f.key === this.selectedKey) || null
    }
  },
  created() {
    if (this.formId) this.load(this.formId)
    this.loadRoles()
    this.loadCenters()
  },
  methods: {
    typeLabel,
    // 成员字段：部门范围提示文案（展示所关联的部门字段标题）
    deptFilterLabel(f) {
      const dep = this.fields.find(x => x.key === (f && f.deptFilterKey))
      return dep ? ('限「' + (dep.label || dep.key) + '」') : '限部门'
    },
    // 部门字段：是否配置了中心范围
    hasCenterScope(f) {
      return !!(f && Array.isArray(f.centerScope) && f.centerScope.length)
    },
    // 部门字段：中心范围提示文案（1 个展示名称，多个展示数量）
    deptCenterLabel(f) {
      const ids = (f && Array.isArray(f.centerScope)) ? f.centerScope : []
      if (!ids.length) return ''
      if (ids.length === 1) {
        const c = (this.centerOptions || []).find(x => String(x.id) === String(ids[0]))
        return c ? ('限「' + c.name + '」') : '限中心'
      }
      return '限' + ids.length + '个中心'
    },
    // 明细列宽：拖动表头分隔线调整（写入 columns[].width，随 formJson 保存）
    startColResize(f, ci, e) {
      const c = (f.columns || [])[ci]
      if (!c) return
      const el = e.currentTarget
      const th = el && el.parentNode ? el.parentNode : null
      const head = th && th.parentNode ? th.parentNode : null
      const startX = e.clientX
      const startW = th ? th.offsetWidth : (Number(c.width) > 0 ? Number(c.width) : 110)
      // 其余列各至少留 60px
      const others = Math.max(0, (f.columns || []).length - 1)
      const headW = head ? head.clientWidth : 0
      const maxW = headW ? Math.max(60, headW - others * 60) : 1200
      const onMove = (ev) => {
        let w = Math.round(startW + (ev.clientX - startX))
        if (w < 60) w = 60
        if (w > maxW) w = maxW
        this.$set(c, 'width', w)
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        this.resizingColKey = ''
      }
      this.resizingColKey = f.key + '_' + ci
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    // 附件：允许类型提示文案（未配置类型时为空串）
    uploadTip(f) {
      return attachTipText(f)
    },
    // 商机/项目条件数据范围：加载角色列表（供「用户角色」字段取值选项）
    loadRoles() {
      getTRoleSelectByPage({ page: 1, limit: 999, roleName: '' }).then(res => {
        if (res && res.code === 200) this.roleOptions = res.data || []
      }).catch(() => {})
    },
    // 部门字段「中心范围」选项：取部门树中启用的顶级组织（中心）；
    // 接口 /department/tree 返回树结构，一级节点即顶级组织；停用节点（is_valid≠1）不参与选择
    loadCenters() {
      getDepartmentList().then(res => {
        if (!res || res.code !== 200) return
        const list = Array.isArray(res.data) ? res.data : []
        this.centerOptions = list
          .filter(n => n && n.id != null && (n.isValid === undefined || n.isValid === 1))
          .map(n => ({ id: n.id, name: n.name }))
      }).catch(() => {})
    },
    load(id) {
      getForm(id).then(res => {
        if (res && res.code === 200 && res.data) {
          const d = res.data
          this.id = d.id
          this.formName = d.formName
          this.remark = d.remark
          this.status = d.status
          this.version = d.version
          try {
            const schema = d.formJson ? JSON.parse(d.formJson) : { fields: [] }
            this.fields = this.normalizeFields(Array.isArray(schema.fields) ? schema.fields : [])
          } catch (e) {
            this.fields = []
          }
        }
      })
    },
    // 兼容旧表单：补齐后加属性，保证属性面板 / 画布徽标能响应其变更
    // （字段对象上一开始不存在该键时，Vue2 读取不到依赖，$set 后画布不会即时刷新）
    normalizeFields(list) {
      ;(list || []).forEach(f => {
        if (!f) return
        if (f.type === 'dept' && !Array.isArray(f.centerScope)) f.centerScope = []
        if (f.type === 'user') {
          if (typeof f.deptFilterKey !== 'string') f.deptFilterKey = ''
          if (f.deptIncludeChildren === undefined) f.deptIncludeChildren = true
        }
      })
      return list
    },
    cloneField(item) {
      return createField(item.type)
    },
    addField(type) {
      const f = createField(type)
      this.fields.push(f)
      this.selectedKey = f.key
    },
    selectField(f) {
      this.selectedKey = f.key
    },
    copyField(idx) {
      const src = this.fields[idx]
      const copy = JSON.parse(JSON.stringify(src))
      copy.key = src.key + '_copy' + Math.floor(Math.random() * 90 + 10)
      copy.label = src.label + '(副本)'
      this.fields.splice(idx + 1, 0, copy)
      this.selectedKey = copy.key
    },
    removeField(idx) {
      const [removed] = this.fields.splice(idx, 1)
      if (removed && removed.key === this.selectedKey) this.selectedKey = ''
    },
    removeSelected() {
      const idx = this.fields.findIndex(f => f.key === this.selectedKey)
      if (idx >= 0) this.removeField(idx)
    },
    validate() {
      if (!this.formName || !this.formName.trim()) {
        this.$message.warning('请填写表单名称')
        return false
      }
      const keys = {}
      for (const f of this.fields) {
        if (f.type === 'tips') continue
        if (!f.key || !f.key.trim()) {
          this.$message.warning('字段「' + f.label + '」缺少 key')
          return false
        }
        if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(f.key)) {
          this.$message.warning('字段 key「' + f.key + '」需以字母开头，仅含字母/数字/下划线')
          return false
        }
        if (keys[f.key]) {
          this.$message.warning('字段 key 重复：' + f.key)
          return false
        }
        keys[f.key] = true
        if (!f.label || !f.label.trim()) {
          this.$message.warning('存在字段缺少标题')
          return false
        }
        if (f.type === 'detail') {
          const cols = f.columns || []
          if (!cols.length) {
            this.$message.warning('明细字段「' + f.label + '」未配置明细列')
            return false
          }
          const colKeys = {}
          for (const c of cols) {
            if (!c.key || !/^[A-Za-z][A-Za-z0-9_]*$/.test(c.key)) {
              this.$message.warning('明细列「' + (c.label || '') + '」的 key「' + (c.key || '') + '」需以字母开头，仅含字母/数字/下划线')
              return false
            }
            if (colKeys[c.key]) {
              this.$message.warning('明细列 key 重复：' + c.key)
              return false
            }
            colKeys[c.key] = true
            if (!c.label || !c.label.trim()) {
              this.$message.warning('明细字段「' + f.label + '」存在子列缺少标题')
              return false
            }
            if (c.type === 'cascade') {
              if (!c.sql || !c.sql.trim()) {
                this.$message.warning('明细字段「' + f.label + '」的级联子列「' + c.label + '」未配置数据源SQL')
                return false
              }
              if (!(c.levels || []).length) {
                this.$message.warning('明细字段「' + f.label + '」的级联子列「' + c.label + '」未配置层级映射，请在属性面板点击「解析」')
                return false
              }
            }
            if (c.type === 'sqlValue' && (!c.sql || !c.sql.trim())) {
              this.$message.warning('明细字段「' + f.label + '」的取值子列「' + (c.label || '') + '」未配置取值SQL')
              return false
            }
            for (const ct of (c.type === 'ctrlNumber' ? (c.constraints || []) : [])) {
              if (ct.mode === 'ref' && !ct.refKey) {
                this.$message.warning('明细字段「' + f.label + '」的受控数字子列「' + (c.label || '') + '」存在引用值约束未填引用key')
                return false
              }
              if (ct.mode === 'fixed' && (ct.value == null || ct.value === '')) {
                this.$message.warning('明细字段「' + f.label + '」的受控数字子列「' + (c.label || '') + '」存在固定值约束未填数值')
                return false
              }
            }
          }
        }
        if (f.type === 'cascade') {
          if (!f.sql || !f.sql.trim()) {
            this.$message.warning('级联字段「' + f.label + '」未配置数据源SQL')
            return false
          }
          if (!(f.levels || []).length) {
            this.$message.warning('级联字段「' + f.label + '」未配置层级映射，请在属性面板点击「解析并预览」')
            return false
          }
        }
        if (f.type === 'total') {
          const d = this.fields.find(x => x.key === f.detailKey && x.type === 'detail')
          if (!d) {
            this.$message.warning('总价字段「' + f.label + '」未选择关联明细字段')
            return false
          }
          if (!f.qtyCol || !f.priceCol) {
            this.$message.warning('总价字段「' + f.label + '」未配置数量列/单价列')
            return false
          }
        }
        if (f.type === 'sqlValue' && (!f.sql || !f.sql.trim())) {
          this.$message.warning('取值字段「' + f.label + '」未配置取值SQL')
          return false
        }
        if ((f.type === 'chance' || f.type === 'project') && Array.isArray(f.scopeRules)) {
          for (const r of f.scopeRules) {
            if (!r.key) {
              this.$message.warning('「' + f.label + '」的条件数据范围存在未选择字段的规则')
              return false
            }
          }
        }
        for (const ct of (f.type === 'ctrlNumber' ? (f.constraints || []) : [])) {
          if (ct.mode === 'ref' && !ct.refKey) {
            this.$message.warning('受控数字字段「' + f.label + '」存在引用值约束未填引用key')
            return false
          }
          if (ct.mode === 'fixed' && (ct.value == null || ct.value === '')) {
            this.$message.warning('受控数字字段「' + f.label + '」存在固定值约束未填数值')
            return false
          }
        }
      }
      // 只读文本：${字段key} 引用无效（不存在或指向只读文本）时提示（不阻断保存，避免历史表单因引用失效无法保存）
      const badRefs = []
      this.fields.forEach(f => {
        if (f.type !== 'readonlyText') return
        readonlyTextPlaceholders(f.content).forEach(k => {
          const hit = this.fields.some(x => x.key === k && x.type !== 'readonlyText' && x.key !== f.key)
          if (!hit) badRefs.push('「' + (f.label || f.key) + '」→ ${' + k + '}')
        })
      })
      if (badRefs.length) this.$message.warning('只读文本引用了无效字段：' + badRefs.join('、'))
      return true
    },
    save() {
      if (!this.validate()) return
      this.saving = true
      const payload = {
        id: this.id,
        formName: this.formName.trim(),
        remark: this.remark,
        status: this.status,
        version: this.version,
        formJson: JSON.stringify({ fields: this.fields })
      }
      saveForm(payload).then(res => {
        this.saving = false
        if (res && res.code === 200) {
          this.$message.success('保存成功')
          if (res.data && res.data.id) this.id = res.data.id
          this.$emit('saved')
        }
      }).catch(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.wf-form-designer {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.wf-toolbar {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #EBEEF5;
  background: #fff;
}
.wf-toolbar-right {
  margin-left: auto;
}
.wf-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.wf-palette {
  width: 220px;
  border-right: 1px solid #EBEEF5;
  overflow-y: auto;
  padding: 10px;
  background: #FAFAFA;
}
.wf-palette-title {
  font-size: 12px;
  color: #909399;
  margin: 8px 0 6px;
}
.wf-palette-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wf-palette-item {
  width: calc(50% - 4px);
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 8px 6px;
  font-size: 12px;
  color: #606266;
  cursor: move;
  text-align: center;
}
.wf-palette-item:hover {
  border-color: #409EFF;
  color: #409EFF;
}
.wf-canvas-wrap {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  background: #F5F7FA;
}
.wf-canvas-title {
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}
.wf-canvas {
  max-width: 640px;
  margin: 0 auto;
  min-height: 200px;
}
.wf-canvas-item {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #EBEEF5;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
  position: relative;
}
.wf-canvas-item.active {
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}
.wf-drag-handle {
  color: #C0C4CC;
  cursor: move;
  margin-right: 10px;
}
.wf-canvas-main {
  flex: 1;
}
.wf-canvas-label {
  font-size: 13px;
  color: #303133;
  margin-bottom: 6px;
}
.wf-req {
  color: #F56C6C;
  margin-right: 4px;
}
.wf-canvas-money {
  display: block;
}
.wf-canvas-money .wf-unit {
  margin-left: 8px;
  color: #909399;
  font-style: normal;
}
.wf-money-upper {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.wf-canvas-upload {
  display: block;
}
.wf-canvas-upload .wf-upload-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.wf-cond-mark {
  margin-left: 6px;
  padding: 0 4px;
  font-size: 11px;
  font-style: normal;
  color: #E6A23C;
  border: 1px solid #F5DAB1;
  border-radius: 3px;
}
.wf-canvas-ops {
  display: none;
  gap: 10px;
}
.wf-canvas-item:hover .wf-canvas-ops {
  display: flex;
}
.wf-canvas-ops i {
  color: #909399;
  cursor: pointer;
}
.wf-canvas-ops i:hover {
  color: #409EFF;
}
.wf-canvas-empty {
  text-align: center;
  color: #C0C4CC;
  padding: 60px 0;
  border: 1px dashed #DCDFE6;
  border-radius: 6px;
  max-width: 640px;
  margin: 0 auto;
}
.wf-config {
  width: 450px;
  border-left: 1px solid #EBEEF5;
  overflow-y: auto;
  background: #fff;
}
.wf-tips-preview {
  color: #909399;
  font-size: 12px;
}
.wf-detail-preview {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}
.wf-detail-head {
  display: flex;
  background: #F5F7FA;
  border-bottom: 1px solid #EBEEF5;
}
.wf-detail-th {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
  padding: 6px 8px;
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 表头列宽拖拽条 */
.wf-col-resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 2;
  user-select: none;
}
.wf-col-resizer::after {
  content: '';
  position: absolute;
  top: 0;
  left: 3px;
  width: 2px;
  height: 100%;
  background: transparent;
}
.wf-col-resizer:hover::after,
.wf-col-resizer.active::after {
  background: #409EFF;
}
.wf-detail-add {
  padding: 8px;
  text-align: center;
  color: #409EFF;
  font-size: 12px;
}
</style>
